import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ShieldCheck, KeyRound, RotateCcw } from 'lucide-react';

/**
 * MFAVerify — shown after login when admin has MFA enabled.
 * Reads challenge_token from sessionStorage, verifies TOTP or backup code.
 */
function MFAVerify({ onLogin }) {
  const [code, setCode] = useState('');
  const [useBackup, setUseBackup] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const challengeToken = sessionStorage.getItem('mfa_challenge_token');
  const mfaEmail = sessionStorage.getItem('mfa_email') || '';

  useEffect(() => {
    if (!challengeToken) {
      navigate('/tf-admin-auth', { replace: true });
      return;
    }
    if (inputRef.current) inputRef.current.focus();
  }, [challengeToken, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = code.trim().replace(/\s/g, '');
    if (!trimmed) {
      toast.error('Please enter a code');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/mfa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challenge_token: challengeToken, code: trimmed.toUpperCase() }),
      });
      const data = await response.json();
      if (response.ok) {
        sessionStorage.removeItem('mfa_challenge_token');
        sessionStorage.removeItem('mfa_email');
        toast.success('Verified! Welcome back.');
        onLogin(data.user, data.token);
        const returnUrl = sessionStorage.getItem('returnUrl');
        if (returnUrl) {
          sessionStorage.removeItem('returnUrl');
          navigate(returnUrl, { replace: true });
        } else {
          navigate('/browse', { replace: true });
        }
      } else {
        toast.error(data.detail || 'Invalid code. Please try again.');
        setCode('');
        if (inputRef.current) inputRef.current.focus();
      }
    } catch {
      toast.error('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#1B5E5F', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <header style={{ backgroundColor: '#1B5E5F', borderBottom: '1px solid rgba(255,255,255,0.1)', padding: '16px 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img src="/technofocus-logo.png" alt="Technofocus" style={{ height: '20px', filter: 'brightness(0) invert(1)' }} />
            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '20px', fontWeight: '200' }}>|</span>
            <img src="/levelup-logo-light.svg" alt="LevelUp" style={{ height: '20px' }} />
          </div>
          <span style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#FFF', fontSize: '11px', fontWeight: '600', padding: '4px 10px', borderRadius: '12px', textTransform: 'uppercase' }}>
            2-Factor Authentication
          </span>
        </div>
      </header>

      <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
        <div style={{ width: '100%', maxWidth: '420px' }}>
          <div style={{ backgroundColor: '#FFFFFF', borderRadius: '12px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
            <div style={{ padding: '40px 32px' }}>
              {/* Icon */}
              <div style={{ textAlign: 'center', marginBottom: '28px' }}>
                <div style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: '#E8F5E9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                  <ShieldCheck size={32} color="#1B5E5F" />
                </div>
                <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#1F2937', marginBottom: '8px' }}>
                  Two-Factor Authentication
                </h1>
                <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: '1.5' }}>
                  {useBackup
                    ? 'Enter one of your saved backup codes.'
                    : <>Open your authenticator app and enter the 6-digit code{mfaEmail ? <> for <strong>{mfaEmail}</strong></> : ''}.</>
                  }
                </p>
              </div>

              <form onSubmit={handleSubmit} data-testid="mfa-verify-form">
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#1F2937', marginBottom: '8px' }}>
                    {useBackup ? 'Backup Code' : 'Authenticator Code'}
                  </label>
                  <input
                    ref={inputRef}
                    type={useBackup ? 'text' : 'text'}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    maxLength={useBackup ? 10 : 6}
                    placeholder={useBackup ? 'XXXX-XXXXX' : '000000'}
                    autoComplete="one-time-code"
                    data-testid="mfa-code-input"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: useBackup ? '16px' : '24px',
                      fontFamily: 'monospace',
                      letterSpacing: useBackup ? '2px' : '8px',
                      textAlign: 'center',
                      color: '#1F2937',
                      outline: 'none',
                      boxSizing: 'border-box',
                      transition: 'border-color 0.2s',
                    }}
                    onFocus={(e) => { e.target.style.borderColor = '#1B5E5F'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#D1D5DB'; }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="mfa-verify-submit"
                  style={{
                    width: '100%',
                    padding: '14px',
                    backgroundColor: loading ? '#6B7280' : '#1B5E5F',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s',
                  }}
                >
                  {loading ? 'Verifying...' : 'Verify & Sign In'}
                </button>
              </form>

              {/* Toggle backup code */}
              <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <button
                  onClick={() => { setUseBackup(!useBackup); setCode(''); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1B5E5F', fontSize: '13px', fontWeight: '500', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                  data-testid="mfa-toggle-backup"
                >
                  {useBackup ? <><KeyRound size={14} /> Use authenticator app instead</> : <><RotateCcw size={14} /> Use a backup code instead</>}
                </button>
              </div>

              {/* Help */}
              <div style={{ marginTop: '24px', padding: '16px', backgroundColor: '#F0FDF4', borderRadius: '8px', border: '1px solid #BBF7D0' }}>
                <p style={{ fontSize: '12px', color: '#166534', margin: 0, lineHeight: '1.6' }}>
                  <strong>Microsoft Authenticator / Google Authenticator:</strong> Open the app on your phone and look for <em>Compete Content Hub</em> to get your 6-digit code. Codes refresh every 30 seconds.
                </p>
              </div>
            </div>
          </div>

          <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '12px', color: '#B2DFDB' }}>
            Having trouble? Contact your portal administrator.
          </p>
        </div>
      </main>
    </div>
  );
}

export default MFAVerify;
